import { useState, useRef, useEffect } from "react";
import { Plus } from "lucide-react";
import { SB } from "@/config/sidebarSizing";
import { THEME } from "@/config/theme";
import { getProjectInitials, getProjectAccent, activeCircleStyle } from "@/utils/projectInitials";
import RowMenu from "./RowMenu";
import type { ApiProject } from "@/services/projectService";

interface Props {
  projects: ApiProject[];
  loading: boolean;
  error: string | null;
  activeId?: string | null;
  onSelect: (projectId: string) => void;
  onRename: (projectId: string, name: string) => void;
  onDelete: (projectId: string) => void;
  onCreate: (name: string) => Promise<void>;
}

function WorkspaceRow({
  project,
  active,
  onSelect,
  onRename,
  onDelete,
}: {
  project: ApiProject;
  active: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(project.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    setEditName(project.name);
  }, [project.name]);

  const commitEdit = () => {
    setEditing(false);
    const name = editName.trim();
    if (name && name !== project.name) {
      onRename(name);
    } else {
      setEditName(project.name);
    }
  };

  // Per-workspace accent (mock `w.accent`) — colours the active row bar/circle so
  // peeking different workspaces reads as different colours.
  const accent = getProjectAccent(project.id);

  return (
    <div
      onClick={() => !editing && onSelect()}
      aria-current={active ? "true" : undefined}
      style={active ? { backgroundColor: `${accent}14` } : undefined}
      className={`relative w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl transition-colors duration-150 cursor-pointer group font-sidebar ${
        active
          ? "text-slate-50 font-semibold"
          : `${THEME.sidebar.rowText} ${THEME.sidebar.rowHover}`
      }`}
    >
      {/* Active accent bar (mock `barStyle`) — a short tab on the row's left edge. */}
      {active && (
        <span
          aria-hidden
          style={{ backgroundColor: accent }}
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r"
        />
      )}
      {/* Circle badge (mock `circleStyle`) — neutral fill + initials, with the
          workspace's accent tint/border/glow when active. */}
      <span
        aria-hidden
        style={active ? activeCircleStyle(accent) : undefined}
        className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-[12px] font-bold tracking-tight ${
          active ? "border text-slate-50" : THEME.sidebar.circleIdle
        }`}
      >
        {getProjectInitials(project.name)}
      </span>
      {editing ? (
        <input
          ref={inputRef}
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitEdit();
            if (e.key === "Escape") { setEditing(false); setEditName(project.name); }
          }}
          onClick={(e) => e.stopPropagation()}
          className={`flex-1 text-[13px] font-sidebar ${THEME.sidebar.input} rounded px-1 py-0 min-w-0`}
        />
      ) : (
        <span
          className="text-[13px] font-semibold font-sidebar truncate flex-1"
          onDoubleClick={(e) => { e.stopPropagation(); setEditName(project.name); setEditing(true); }}
          title="Double-click to rename"
        >
          {project.name}
        </span>
      )}
      <RowMenu items={[
        { label: "Rename", onClick: () => { setEditName(project.name); setEditing(true); } },
        { label: "Delete", onClick: onDelete, danger: true },
      ]} />
    </div>
  );
}

export default function WorkspaceList({ projects, loading, error, activeId = null, onSelect, onRename, onDelete, onCreate }: Props) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (creating) inputRef.current?.focus();
  }, [creating]);

  const startCreating = () => {
    setNewName("Untitled Workspace");
    setCreating(true);
  };

  const commitCreate = async () => {
    const name = newName.trim();
    setCreating(false);
    if (!name) return;
    await onCreate(name);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Row header: the parent rail already shows the "WORKSPACES" section label, so this
          is just the create affordance, right-aligned (clipped off the slim
          collapsed rail; visible once the rail opens). */}
      <div className={`flex items-center justify-end px-2.5 pt-2 pb-1.5 shrink-0`}>
        <button
          onClick={startCreating}
          title="New workspace"
          className="p-0.5 rounded text-slate-400 hover:text-slate-100 transition-colors"
        >
          <Plus size={SB.icon.headerAdd} />
        </button>
      </div>

      {/* overflow-x-hidden: in the collapsed 58px rail a row's min width (circle +
          padding) exceeds the rail, which would otherwise surface a horizontal
          scrollbar (overflow-y:auto makes the x-axis compute to auto). No-op when
          expanded since the rows fit. */}
      <div className={`flex-1 overflow-y-auto overflow-x-hidden ${SB.row.list} ${THEME.scrollbar.dark}`}>
        {creating && (
          <div className="px-2 py-1">
            <input
              ref={inputRef}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={commitCreate}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitCreate();
                if (e.key === "Escape") setCreating(false);
              }}
              // Match the created workspace rows' name typography (13px semibold),
              // not the larger header size, so the "Untitled Workspace" you type
              // reads exactly like the rows it becomes.
              className={`w-full text-[13px] font-semibold font-sidebar ${THEME.sidebar.input} rounded px-1.5 py-0.5`}
            />
          </div>
        )}
        {loading && <p className={`${SB.text.primary} text-canvas-muted px-2 py-2`}>Loading…</p>}
        {error && <p className={`${SB.text.primary} text-red-400 px-2 py-2 break-words`}>{error}</p>}
        {projects.map((project) => (
          <WorkspaceRow
            key={project.id}
            project={project}
            active={project.id === activeId}
            onSelect={() => onSelect(project.id)}
            onRename={(name) => onRename(project.id, name)}
            onDelete={() => onDelete(project.id)}
          />
        ))}
      </div>
    </div>
  );
}
